/* eslint-disable */
import React from 'react';
import type { Coordinates } from '@volkswagen-onehub/audi-feature-service-visitor-locator';
import type { Dealer } from '../../../models/dealer';
import mapContext from '../../../contexts/map-context';
import { ActionTypes } from '../../../contexts/reducers/map-reducer';
import loggerStateContext from '../../../contexts/logger-context';
import {
  DEFAULT_CENTER_COORDINATES,
  DEFAULT_Y_OFFSET,
  DEFAULT_ZOOM,
  DEFAULT_ZOOM_ON_DEALER,
} from './MapUtils';
import { useMapProperty } from '../../../hooks/useMapProperty';
import { useHashParams } from '../../../hooks/useHashParams';

export const PRE_SELECTED_HASH_VALUE = '#filters';

export const useSetSearchInThisAreaVisible = () => {
  const dispatch = mapContext.useMapDispatch();

  return (visibility: boolean) => {
    dispatch({ type: ActionTypes.setSearchInThisAreaVisible, payload: visibility });
  };
};

export const useSetMapZoomAndCenterCoordinates = () => {
  const dispatch = mapContext.useMapDispatch();

  return (zoom: number, coordinates: Coordinates) => {
    dispatch({ type: ActionTypes.setMapZoom, payload: zoom });
    dispatch({
      type: ActionTypes.setMapCenterCoordinates,
      payload: coordinates,
    });
  };
};

export const useLogger = () => {
  const { logger } = loggerStateContext.useLoggerState();
  const { searchRadius } = mapContext.useMapState();

  React.useEffect(() => {
    // TODO send radius as parameter when it's implemented in the backend
    logger.debug(`Radius is :${searchRadius}`);
  }, []);
};

export const useOnChangeDealerId = () => {
  const { selectedDealerId, results: dealers } = mapContext.useMapState();
  const { calculateMapZoomLevel, calculateMapCenterForDealers } = useMapProperty();
  const setMapZoomAndCenterCoordinates = useSetMapZoomAndCenterCoordinates();

  React.useEffect(() => {
    if (selectedDealerId && dealers?.length) {
      const dealer = dealers.find((d: Dealer) => d.dealerId === selectedDealerId);

      if (dealer) {
        setMapZoomAndCenterCoordinates(DEFAULT_ZOOM_ON_DEALER, {
          lat: dealer.latitude + DEFAULT_Y_OFFSET,
          lng: dealer.longitude,
        });
      }
    } else if (!selectedDealerId && dealers?.length) {
      setMapZoomAndCenterCoordinates(calculateMapZoomLevel(), calculateMapCenterForDealers());
    }
  }, [selectedDealerId, dealers === null]);
};

export const useOnChangeDealerList = () => {
  const { results: dealers, selectedDealerId } = mapContext.useMapState();
  const { calculateMapZoomLevel, calculateMapCenterForDealers } = useMapProperty();
  const setMapZoomAndCenterCoordinates = useSetMapZoomAndCenterCoordinates();
  const setSearchInThisAreaVisible = useSetSearchInThisAreaVisible();

  React.useEffect(() => {
    const zoom = calculateMapZoomLevel();
    const center = calculateMapCenterForDealers();

    if (dealers?.length && !selectedDealerId) {
      setMapZoomAndCenterCoordinates(zoom, center);
    } else if (dealers === null) {
      setMapZoomAndCenterCoordinates(DEFAULT_ZOOM, DEFAULT_CENTER_COORDINATES);
    }

    setSearchInThisAreaVisible(false);
  }, [dealers]);
};

export const useOnChangeHash = () => {
  const [hash, resetHash] = useHashParams();
  const dispatch = mapContext.useMapDispatch();
  React.useEffect(() => {
    if (hash === PRE_SELECTED_HASH_VALUE) {
      dispatch({ type: ActionTypes.setIsFiltersOverlayOpen, payload: true });
      resetHash('');
    }
  }, []);
};
